import { TtlCacheStore, TtlEntry } from 'switch-shared/utils/ttl-cache';

/**
 * A `sessionStorage`-backed store for `TtlCache`. Entries are namespaced by a
 * prefix so a single cache can be cleared without touching unrelated keys, and
 * all storage access is wrapped in try/catch — a disabled or full store degrades
 * to a cache miss rather than throwing.
 */
export class SessionStorageTtlStore<T> implements TtlCacheStore<T> {
  private readonly prefix: string;

  private readonly parsedEntries = new Map<
    string,
    { raw: string; entry: TtlEntry<T> }
  >();

  constructor(namespace: string) {
    this.prefix = `${namespace}:`;
  }

  private storageKey(key: string) {
    return `${this.prefix}${key}`;
  }

  get(key: string): TtlEntry<T> | undefined {
    const storageKey = this.storageKey(key);
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) {
        this.parsedEntries.delete(storageKey);
        return undefined;
      }

      const parsedEntry = this.parsedEntries.get(storageKey);
      if (parsedEntry?.raw === raw) {
        return parsedEntry.entry;
      }

      const entry = JSON.parse(raw) as TtlEntry<T>;
      this.parsedEntries.set(storageKey, { raw, entry });
      return entry;
    } catch {
      this.parsedEntries.delete(storageKey);
      return undefined;
    }
  }

  set(key: string, entry: TtlEntry<T>) {
    const storageKey = this.storageKey(key);
    try {
      const raw = JSON.stringify(entry);
      sessionStorage.setItem(storageKey, raw);
      this.parsedEntries.set(storageKey, { raw, entry });
    } catch {
      // Ignore quota/serialization errors — the cache is best-effort.
    }
  }

  delete(key: string) {
    const storageKey = this.storageKey(key);
    this.parsedEntries.delete(storageKey);
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // Ignore.
    }
  }

  clear() {
    this.parsedEntries.clear();
    try {
      const keys: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(this.prefix)) {
          keys.push(key);
        }
      }
      keys.forEach((key) => sessionStorage.removeItem(key));
    } catch {
      // Ignore.
    }
  }

  size() {
    let count = 0;
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        if (sessionStorage.key(i)?.startsWith(this.prefix)) {
          count++;
        }
      }
    } catch {
      // Ignore.
    }
    return count;
  }
}
